import type { LocalTradeFilters } from '@/types';
import { COLLATERAL_TYPE_LABELS, TRADE_STATUS_LABELS } from '@/types';
import type { AppliedFilterChip } from '@/utils/inventory/filter-chip-labels';
import { formatMultiSelectValue } from '@/utils/filters/format-multi-select-value';

export interface TradesFilterOptionSets {
  statuses?: string[];
  counterparties?: string[];
  symbols?: string[];
  tradeTypes?: string[];
  collateralTypes?: string[];
  agreementTypes?: string[];
  termTypes?: string[];
  currencies?: string[];
}

type FilterChipDef = {
  filterId: string;
  label: string;
  isActive: (f: LocalTradeFilters) => boolean;
  getValue: (f: LocalTradeFilters, opts?: TradesFilterOptionSets) => string;
  /** False when the chip must not offer a click-to-open-sidebar affordance
   *  (its filter UI lives elsewhere — see the settlement-ccy pill). */
  clickable?: boolean;
};

const CHIP_DEFS: FilterChipDef[] = [
  {
    filterId: 'statuses',
    label: 'Status',
    isActive: (f) => f.statuses.length > 0,
    // LR-497: show human labels (Pending Trade Date / Partially Settled / …),
    // not raw enum keys. Self-contained like collateralTypes below — the option
    // pool is the full label set so "all" / "all except" collapse still works.
    getValue: (f) =>
      formatMultiSelectValue(
        f.statuses.map((v) => TRADE_STATUS_LABELS[v]),
        Object.values(TRADE_STATUS_LABELS),
      ),
  },
  {
    filterId: 'counterparties',
    label: 'Counterparty',
    isActive: (f) => f.counterparties.length > 0,
    getValue: (f, opts) => formatMultiSelectValue(f.counterparties, opts?.counterparties),
  },
  {
    filterId: 'symbols',
    label: 'Security',
    isActive: (f) => f.symbols.length > 0,
    getValue: (f, opts) => formatMultiSelectValue(f.symbols, opts?.symbols),
  },
  {
    filterId: 'tradeTypes',
    label: 'Trade Type',
    isActive: (f) => f.tradeTypes.length > 0,
    getValue: (f, opts) => formatMultiSelectValue(f.tradeTypes, opts?.tradeTypes),
  },
  {
    filterId: 'collateralTypes',
    label: 'Collateral Type',
    isActive: (f) => f.collateralTypes.length > 0,
    // Display the human-readable labels (Cash / Non-Cash / Uncollateralized), not
    // the raw enum. The option pool is the full label set so "all" / "all except"
    // collapse works the same as the sibling categorical chips (LR-444).
    getValue: (f) =>
      formatMultiSelectValue(
        f.collateralTypes.map((v) => COLLATERAL_TYPE_LABELS[v]),
        Object.values(COLLATERAL_TYPE_LABELS),
      ),
  },
  {
    filterId: 'agreementTypes',
    label: 'Agreement',
    isActive: (f) => f.agreementTypes.length > 0,
    getValue: (f, opts) => formatMultiSelectValue(f.agreementTypes, opts?.agreementTypes),
  },
  {
    filterId: 'termTypes',
    label: 'Trade Term',
    isActive: (f) => f.termTypes.length > 0,
    getValue: (f, opts) => formatMultiSelectValue(f.termTypes, opts?.termTypes),
  },
  {
    // LR-474: relabelled — this is the sidebar's SECURITY-currency filter,
    // distinct from the settlement-ccy pill chip below (D3: two filters).
    filterId: 'currencies',
    label: 'Security Ccy',
    isActive: (f) => f.currencies.length > 0,
    getValue: (f, opts) => formatMultiSelectValue(f.currencies, opts?.currencies),
  },
  {
    filterId: 'settlementCurrencies',
    label: 'Settlement Ccy',
    isActive: (f) => f.settlementCurrencies.length > 0,
    // No fixed option pool: the pill derives its list from the live book, so the
    // chip lists the raw selection (no "all/all except" collapse).
    getValue: (f) => formatMultiSelectValue(f.settlementCurrencies, undefined),
    // The filter's UI is the summary-bar pill, not a sidebar section — a
    // clickable chip would open the sidebar to nothing (r2 #3 / r3 #2).
    clickable: false,
  },
  {
    filterId: 'tradeDate',
    label: 'Trade Date',
    isActive: (f) => !!(f.tradeDateFrom || f.tradeDateTo),
    getValue: (f) => {
      if (f.tradeDateFrom && f.tradeDateTo) return `${f.tradeDateFrom} – ${f.tradeDateTo}`;
      if (f.tradeDateFrom) return `from ${f.tradeDateFrom}`;
      if (f.tradeDateTo) return `to ${f.tradeDateTo}`;
      return '';
    },
  },
  {
    filterId: 'settlementDate',
    label: 'Settlement Date',
    isActive: (f) => !!(f.settlementDateFrom || f.settlementDateTo),
    getValue: (f) => {
      if (f.settlementDateFrom && f.settlementDateTo) return `${f.settlementDateFrom} – ${f.settlementDateTo}`;
      if (f.settlementDateFrom) return `from ${f.settlementDateFrom}`;
      if (f.settlementDateTo) return `to ${f.settlementDateTo}`;
      return '';
    },
  },
  {
    filterId: 'maturityDate',
    label: 'Maturity Date',
    isActive: (f) => !!(f.maturityDateFrom || f.maturityDateTo),
    getValue: (f) => {
      if (f.maturityDateFrom && f.maturityDateTo) return `${f.maturityDateFrom} – ${f.maturityDateTo}`;
      if (f.maturityDateFrom) return `from ${f.maturityDateFrom}`;
      if (f.maturityDateTo) return `to ${f.maturityDateTo}`;
      return '';
    },
  },
  {
    filterId: 'quantity',
    label: 'Quantity',
    isActive: (f) => f.quantityMin !== null || f.quantityMax !== null,
    getValue: (f) => {
      if (f.quantityMin !== null && f.quantityMax !== null) return `${f.quantityMin}–${f.quantityMax}`;
      if (f.quantityMin !== null) return `≥ ${f.quantityMin}`;
      if (f.quantityMax !== null) return `≤ ${f.quantityMax}`;
      return '';
    },
  },
  {
    filterId: 'contractValue',
    label: 'Trade Margin Value',
    isActive: (f) => f.contractValueMin !== null || f.contractValueMax !== null,
    getValue: (f) => {
      if (f.contractValueMin !== null && f.contractValueMax !== null) return `${f.contractValueMin}–${f.contractValueMax}`;
      if (f.contractValueMin !== null) return `≥ ${f.contractValueMin}`;
      if (f.contractValueMax !== null) return `≤ ${f.contractValueMax}`;
      return '';
    },
  },
  {
    filterId: 'margin',
    label: 'Margin %',
    isActive: (f) => f.marginMin !== null || f.marginMax !== null,
    getValue: (f) => {
      if (f.marginMin !== null && f.marginMax !== null) return `${f.marginMin}–${f.marginMax}`;
      if (f.marginMin !== null) return `≥ ${f.marginMin}`;
      if (f.marginMax !== null) return `≤ ${f.marginMax}`;
      return '';
    },
  },
  {
    filterId: 'grossPrice',
    label: 'Trade Gross Price',
    isActive: (f) => f.grossPriceMin !== null || f.grossPriceMax !== null,
    getValue: (f) => {
      if (f.grossPriceMin !== null && f.grossPriceMax !== null) return `${f.grossPriceMin}–${f.grossPriceMax}`;
      if (f.grossPriceMin !== null) return `≥ ${f.grossPriceMin}`;
      if (f.grossPriceMax !== null) return `≤ ${f.grossPriceMax}`;
      return '';
    },
  },
  {
    filterId: 'dailyEarnings',
    label: 'Daily Earnings',
    isActive: (f) => f.dailyEarningsMin !== null || f.dailyEarningsMax !== null,
    getValue: (f) => {
      if (f.dailyEarningsMin !== null && f.dailyEarningsMax !== null) return `${f.dailyEarningsMin}–${f.dailyEarningsMax}`;
      if (f.dailyEarningsMin !== null) return `≥ ${f.dailyEarningsMin}`;
      if (f.dailyEarningsMax !== null) return `≤ ${f.dailyEarningsMax}`;
      return '';
    },
  },
  {
    filterId: 'createdAt',
    label: 'Created At',
    isActive: (f) => !!(f.createdAtFrom || f.createdAtTo),
    getValue: (f) => {
      if (f.createdAtFrom && f.createdAtTo) return `${f.createdAtFrom} – ${f.createdAtTo}`;
      if (f.createdAtFrom) return `from ${f.createdAtFrom}`;
      if (f.createdAtTo) return `to ${f.createdAtTo}`;
      return '';
    },
  },
  {
    filterId: 'updatedAt',
    label: 'Updated At',
    isActive: (f) => !!(f.updatedAtFrom || f.updatedAtTo),
    getValue: (f) => {
      if (f.updatedAtFrom && f.updatedAtTo) return `${f.updatedAtFrom} – ${f.updatedAtTo}`;
      if (f.updatedAtFrom) return `from ${f.updatedAtFrom}`;
      if (f.updatedAtTo) return `to ${f.updatedAtTo}`;
      return '';
    },
  },
];

export const getAppliedTradesFilterChips = (
  filters: LocalTradeFilters,
  optionSets?: TradesFilterOptionSets
): AppliedFilterChip[] => {
  const chips: AppliedFilterChip[] = [];
  for (const def of CHIP_DEFS) {
    if (!def.isActive(filters)) continue;
    const value = def.getValue(filters, optionSets);
    chips.push({
      filterId: def.filterId,
      label: value ? `${def.label}: ${value}` : def.label,
      ...(def.clickable === false ? { clickable: false } : {}),
    });
  }
  return chips;
};
